


































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import MultiSelect, {
  MultiSelectGroup,
  MultiSelectOption,
  MultiSelectOptions,
} from "@/components/MultiSelect.vue";
import { Getter } from "vuex-class";
import AdcontrolProfile from "@/models/AdcontrolProfile";
import ReportBroadcasterMixin from "@/mixins/http/CustomReport/ReportBroadcasterMixin";
import MultiSelectBroadcaster, {
  Agency,
} from "@/models/MultiSelectBroadcaster";

export type BroadcasterSelectData = {
  broadcasters: Array<MultiSelectBroadcaster>;
  agencies: Array<Agency>;
  allBroadcastersSelected?: boolean;
  allAgenciesSelected?: boolean;
};

@Component({
  components: {
    MultiSelect,
  },
})
export default class ReportBroadcasterSelect extends Mixins(
  ReportBroadcasterMixin
) {
  @Prop({ required: true }) readonly startsAt!: string;
  @Prop({ required: true }) readonly endsAt!: string;
  @Prop({ default: false }) readonly agenciesDisabled!: boolean;
  @Prop({ default: false }) readonly broadcastersDisabled!: boolean;
  @Prop({ default: () => [] })
  readonly initialBroadcasters!: Array<MultiSelectBroadcaster>;
  @Prop({ default: () => [] }) readonly initialAgencies!: Array<Agency>;
  @Prop({ default: true }) readonly clearItemsOnReselect!: boolean;
  @Prop({ default: false }) readonly selectAllBroadcasters!: boolean;
  @Prop({ default: false }) readonly selectAllAgencies!: boolean;
  @Prop({ default: false }) readonly templateMode!: boolean;

  @Getter("profile") profile!: AdcontrolProfile;

  broadcasters: MultiSelectOptions = [];
  selectedBroadcasters: MultiSelectOptions = [];
  selectedAgencies: Agency[] = [];
  agencies: Agency[] = [];
  loading = true;

  @Watch("loading", { immediate: true })
  onLoadingUpdated(value: boolean) {
    this.$emit("update:loading", value);
  }

  @Watch("initialBroadcasters", { immediate: true })
  onInitialBroadcasterUpdated(bc: MultiSelectBroadcaster[]) {
    if (bc === this.selectedBroadcasters) return;
    this.selectedBroadcasters = this.matchSelectionToOptions(
      bc,
      this.broadcasters
    );
  }

  @Watch("initialAgencies", { immediate: true })
  onInitialAgenciesUpdated(agcs: Agency[]) {
    if (agcs === this.selectedAgencies) return;
    this.selectedAgencies = this.matchSelectionToOptions(
      agcs,
      this.agencies
    ) as Agency[];
  }

  async mounted() {
    await this.loadBroadcasters();
    this.selectedBroadcasters = this.matchSelectionToOptions(
      this.initialBroadcasters,
      this.broadcasters
    );
    this.selectedAgencies = this.matchSelectionToOptions(
      this.initialAgencies,
      this.agencies
    ) as Agency[];
    this.loading = false;
    this.$nextTick(() => this.applyInitialSelectAll());
  }

  private matchSelectionToOptions(
    selection: MultiSelectOptions,
    options: MultiSelectOptions
  ): MultiSelectOptions {
    if (!options.length || !selection.length) return selection;
    const optionsById = new Map<number | undefined, MultiSelectOption>();
    for (const option of options) {
      if (!optionsById.has(option.id)) optionsById.set(option.id, option);
    }
    let changed = false;
    const matched = selection.map((item) => {
      const option = optionsById.get(item.id);
      if (option && option !== item) {
        changed = true;
        return option;
      }
      return item;
    });
    return changed ? matched : selection;
  }

  applyInitialSelectAll() {
    if (!this.selectAllBroadcasters && !this.selectAllAgencies) return;
    if (this.selectAllBroadcasters) {
      (this.$refs.broadcastersSelect as MultiSelect | undefined)?.selectAll();
    }
    if (this.selectAllAgencies) {
      (this.$refs.agenciesSelect as MultiSelect | undefined)?.selectAll();
    }
    this.$emit("select-all-applied");
  }

  private isFullSelection(
    selected: MultiSelectOptions | undefined,
    options: MultiSelectOptions
  ): boolean {
    if (!options.length) return false;
    const selectedIds = new Set((selected ?? []).map((element) => element.id));
    return options.every((option) => selectedIds.has(option.id));
  }

  async loadBroadcasters() {
    const type = this.isRadio ? "radio" : "tv";
    const { broadcasters, agencies } = this.templateMode
      ? await this.getTemplateBroadcasters(type)
      : await this.getBroadcasters(type, this.startsAt, this.endsAt);
    this.broadcasters = broadcasters;
    this.agencies = agencies;
  }

  get company() {
    return this.profile.accountCompany;
  }

  get isRadio(): boolean {
    return this.profile.accountType == "radio";
  }

  get groups(): MultiSelectGroup[] {
    let groups = [];

    if (this.company) {
      groups = this.agencies.filter((element) => {
        return element.id != this.company.id;
      });
    } else {
      groups = this.agencies;
    }

    groups.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      } else if (a.name > b.name) {
        return 1;
      } else {
        return 0;
      }
    });

    if (this.company) {
      groups.unshift({
        id: this.company.id,
        name: "Tue emittenti",
      });
    }

    return groups;
  }

  @Watch("selectedBroadcasters")
  onBroadcastersUpdated(broadcasters: MultiSelectOptions) {
    this.$emit("selected", {
      broadcasters,
      agencies: this.selectedAgencies,
      allBroadcastersSelected: this.isFullSelection(
        broadcasters,
        this.broadcasters
      ),
      allAgenciesSelected: this.isFullSelection(
        this.selectedAgencies,
        this.agencies
      ),
    });
    this.selectedBroadcasters = broadcasters;
  }

  @Watch("selectedAgencies")
  onAgenciesUpdated(agencies: MultiSelectOptions) {
    this.$emit("selected", {
      agencies,
      broadcasters: this.selectedBroadcasters,
      allBroadcastersSelected: this.isFullSelection(
        this.selectedBroadcasters,
        this.broadcasters
      ),
      allAgenciesSelected: this.isFullSelection(agencies, this.agencies),
    });
    this.selectedAgencies = agencies as Agency[];
  }

  groupBy(options: MultiSelectOptions, group: MultiSelectGroup) {
    return options.filter((element) => {
      return element.advertising_agency?.id == group.id;
    });
  }
}
